import { Suspense, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import { ErrorBoundary } from "react-error-boundary";
import { PercentagesFleetConfig } from "src/components/PercentagesFleetConfig";
import { PredefinedFleetConfigSelection } from "src/components/PredefinedFleetConfigSelection";
import {
  useLEZ,
  usePercentageFleetsLEZOptions,
  usePredefinedLEZOptions,
  useRestrictionsFleetsLEZOptions,
} from "src/hooks";
import { useScenario } from "src/hooks/useScenario";
import type { FleetType, Scenario } from "src/types";
import { isDisabled } from "src/utils/permission.utils";

import { FleetConfigTable } from "./FleetConfigTable";

interface Props {
  scenarioId: number | string;
  lezId: number | string;
  open: boolean;
  onClose: () => void;
  caseId: number | string;
}

export function FleetConfigDialog(props: Props) {
  const { data: scenario } = useScenario(props.scenarioId);
  const { t } = useTranslation();

  return render();

  function render() {
    if (!scenario) return null;

    return (
      <StyledDialog fullWidth maxWidth="md" open={props.open}>
        <DialogContent>
          <Typography variant="h2" gutterBottom>
            {t("label.vehicleRestrictions")}
          </Typography>
          <Box mb={1}>
            <Typography>
              {t("label.vehicleRestrictionsInstructions")}
            </Typography>
          </Box>
          <ErrorBoundary fallback={null}>
            <Suspense fallback={<CircularProgress />}>
              <LoadData {...props} scenario={scenario} />
            </Suspense>
          </ErrorBoundary>
        </DialogContent>
      </StyledDialog>
    );
  }
}

function LoadData(props: Props & { scenario: Scenario }) {
  const { scenario, lezId, caseId, onClose } = props;
  const { t } = useTranslation();

  const { data: predefinedLEZs } = usePredefinedLEZOptions(caseId);
  const { data: percentageFleets } = usePercentageFleetsLEZOptions(caseId);
  const { data: restrictionsFleets } = useRestrictionsFleetsLEZOptions(caseId);

  const { data: lez } = useLEZ(scenario.id, lezId);

  const [selectedFleetOption, setSelectedFleetOption] = useState<FleetType>(
    lez?.fleetType || "RESTRICTIONS"
  );

  const isReadOnly = isDisabled(scenario.permissions.updateLEZ);

  const restrictionFleetNameKey =
    lez?.restrictionFleetNameKey || "fleet.default";
  const percentageFleetNameKey = lez?.percentageFleetNameKey || "fleet.default";
  
  return (
    <>
      <Tabs
        value={selectedFleetOption}
        onChange={(_, newValue) => setSelectedFleetOption(newValue)}
      >
        <Tab label={t("label.defineCustomRestrictions")} value="RESTRICTIONS" />
        <Tab label={t("label.usePredefinedFleet")} value="PREDEFINED" />
        <Tab label={t("label.percentagesFleet")} value="PERCENTAGES" />
      </Tabs>
      {selectedFleetOption === "RESTRICTIONS" && (
        <Suspense fallback={<CircularProgress />}>
          <FleetConfigTable
            options={restrictionsFleets || []}
            className="fleetConfig"
            lezId={lezId}
            scenarioId={scenario.id}
            restrictionFleetNameKey={restrictionFleetNameKey}
            readOnly={isReadOnly}
            onClose={onClose}
          />
        </Suspense>
      )}
      {selectedFleetOption === "PERCENTAGES" && (
        <PercentagesFleetConfig
          lezId={lezId}
          options={percentageFleets || []}
          scenarioId={scenario.id}
          onClose={onClose}
          readOnly={isReadOnly}
          percentageFleetNameKey={percentageFleetNameKey}
        />
      )}
      {selectedFleetOption === "PREDEFINED" && (
        <PredefinedFleetConfigSelection
          defaultValue={lez?.predefinedFleetNameKey || ""}
          options={predefinedLEZs || []}
          readOnly={isDisabled(scenario.permissions.updateLEZ)}
          lezId={lezId}
          scenarioId={scenario.id}
          onClose={onClose}
        />
      )}
    </>
  );
}

const StyledDialog = styled(Dialog)`
  .MuiDialogActions-root {
    margin-top: auto;
    padding: ${({ theme }) => theme.spacing(3)};
  }
  .MuiCircularProgress-root {
    margin: auto;
  }
  .MuiDialogContent-root {
    display: flex;
    flex-direction: column;
    overflow: auto;
    height: 100vh;
  }
  /* .MuiDialogContent-root > *:last-child {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .MuiDialogContent-root > *:last-child > * {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  } */

  .MuiRadio-root {
    padding: 4px 4px;
  }
  .MuiFormControl-root {
    margin-bottom: 16px;
    margin-left: 4px;
  }

  .MuiTabs-root {
    border-radius: 4px;
    min-height: fit-content;
    margin-top: ${({ theme }) => theme.spacing(1)};
    margin-bottom: ${({ theme }) => theme.spacing(2.5)};
    .MuiTabs-indicator {
      display: none;
    }

    .MuiTab-root {
      min-height: unset;
      text-transform: none;
      font-weight: 500;
      padding: ${({ theme }) => theme.spacing(1, 2)};

      &.Mui-selected {
        background-color: ${({ theme }) => theme.palette.primary.main};

        border-radius: 8px;
        color: white;
        box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
      }
      .MuiSvgIcon-root {
        font-size: 14px;
        transform: translateY(-1px);
      }
    }
  }
`;
